import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../utils/cn';

const sliderVariants = cva('w-full cursor-ew-resize disabled:cursor-not-allowed', {
  variants: {
    variant: {
      default: 'accent-primary/80',
      secondary: 'accent-secondary/80',
      accent: 'accent-accent/80',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

function getSliderBadgeLeft(value: number, min: number, max: number): string {
  if (max <= min) return '0%';
  const normalized = (value - min) / (max - min);
  return `calc(${normalized * 100}% - ${normalized * 16 - 8}px)`;
}

function getSliderStepPositions(
  min: number,
  max: number,
  step: number | string | undefined,
): number[] {
  const stepValue = Number(step ?? 1);

  if (!Number.isFinite(stepValue) || stepValue <= 0 || max <= min) return [];

  return Array.from({ length: Math.floor((max - min) / stepValue) + 1 }, (_, index) => {
    return ((min + index * stepValue - min) / (max - min)) * 100;
  });
}

export interface SliderWithBadgeProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'min' | 'max'>,
    VariantProps<typeof sliderVariants> {
  value: number;
  min: number;
  max: number;
  badgeFormatter?: (value: number) => React.ReactNode;
  showStepMarks?: boolean;
  containerClassName?: string;
  inputClassName?: string;
  badgeClassName?: string;
  onValueChange?: (value: number) => void;
}

const SliderWithBadge = React.forwardRef<HTMLInputElement, SliderWithBadgeProps>(
  (
    {
      value,
      min,
      max,
      disabled,
      variant = 'default',
      badgeFormatter,
      showStepMarks = false,
      containerClassName,
      inputClassName,
      badgeClassName,
      className,
      onChange,
      onValueChange,
      step,
      ...props
    },
    ref,
  ) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event);
      onValueChange?.(Number(event.target.value));
    };

    return (
      <div className={cn('relative flex flex-col pt-6', containerClassName)}>
        <div
          className={cn(
            'pointer-events-none absolute top-0 -translate-x-1/2 rounded-lg px-1.5 py-0.5 text-xs font-bold tabular-nums',
            badgeClassName,
            {
              'bg-primary/80 text-primary-foreground': variant === 'default',
              'bg-secondary/80 text-secondary-foreground': variant === 'secondary',
              'bg-accent/80 text-accent-foreground': variant === 'accent',
            },
          )}
          style={{ left: getSliderBadgeLeft(value, min, max) }}
        >
          {badgeFormatter ? badgeFormatter(value) : value}
        </div>
        <input
          {...props}
          ref={ref}
          className={cn(sliderVariants({ variant, className: cn(inputClassName, className) }))}
          disabled={disabled}
          max={max}
          min={min}
          step={step}
          type="range"
          value={value}
          onChange={handleChange}
        />
        {showStepMarks && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute relative bottom-px h-[5px]"
          >
            {getSliderStepPositions(min, max, step).map((position) => (
              <span
                key={position}
                className="absolute h-[inherit] w-px bg-muted-foreground/50"
                style={{ left: `calc(${position}% + ${8 - (position / 100) * 16}px)` }}
              />
            ))}
          </div>
        )}
      </div>
    );
  },
);

SliderWithBadge.displayName = 'SliderWithBadge';

export { SliderWithBadge };
